import { ACH } from './constants';
import { getCardConnectCardName } from './cards';
import { formatCurrency } from '@/utils/common/formatting';
import { isPaymentMethodBank } from './helpers';

/**
 * Format convenience fee as currency
 */
export const formatConvenienceFee = (fee: number): string => {
  return formatCurrency(Math.round((fee + Number.EPSILON) * 100) / 100);
};

/**
 * Get a human-readable label for a payment method
 * Examples: "Visa *1234", "ACH *5678", "Bank Account"
 * 
 * @param paymentMethod - The payment method object
 * @returns Formatted label string
 */
export const getPaymentMethodLabel = (paymentMethod: any): string => {
  if (!paymentMethod) return 'No payment method';

  const last4 = paymentMethod.last4 || '';

  // Handle bank accounts / ACH
  if (isPaymentMethodBank(paymentMethod) || paymentMethod.type === ACH || paymentMethod.type === 'bank') {
    if (paymentMethod.bankName && last4) {
      return `${paymentMethod.bankName} *${last4}`;
    }
    if (paymentMethod.name && last4) {
      return `${paymentMethod.name} *${last4}`;
    }
    return last4 ? `ACH *${last4}` : 'Bank Account';
  }

  // Handle cards - try CardConnect name first
  const cardTypeName = getCardConnectCardName(paymentMethod.accttype || '');
  if (cardTypeName && last4) {
    return `${cardTypeName} *${last4}`;
  }

  // Fallback to brand or type
  const brand = paymentMethod.brand || paymentMethod.type || 'Card';
  const capitalizedBrand = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  
  return last4 ? `${capitalizedBrand} *${last4}` : capitalizedBrand;
};

/**
 * Get short label for payment method (just brand/type and last4)
 * Examples: "Visa *1234", "ACH *5678"
 */
export const getPaymentMethodShortLabel = (paymentMethod: any): string => {
  if (!paymentMethod) return '';
  
  const last4 = paymentMethod.last4 || '';
  const isBank = isPaymentMethodBank(paymentMethod);
  const brand = paymentMethod.brand || (isBank ? 'ACH' : 'Card');
  
  return last4 ? `${brand} *${last4}` : brand;
};

